import { useState, useEffect, useCallback, useRef } from 'react';
import { extractPlaylistId, getRandomTrack } from '../services/spotifyService';
import MusicPlayer from './MusicPlayer';
import SongInfo from './SongInfo';
import Controls from './Controls';

export default function GameBoard({ accessToken }) {
  const [currentSong, setCurrentSong] = useState(null);
  const [selectedPlaylistId, setSelectedPlaylistId] = useState('');
  const [playlistUrl, setPlaylistUrl] = useState('');
  const [playlistUrlError, setPlaylistUrlError] = useState('');
  const [showPlaylistSetup, setShowPlaylistSetup] = useState(true);
  const [revealed, setRevealed] = useState({
    title: false,
    artist: false,
    year: false,
  });
  const [hasPlayedOnce, setHasPlayedOnce] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const seenTrackIdsRef = useRef(new Set());

  const loadNewSong = useCallback(async () => {
    if (!selectedPlaylistId) {
      setError('Paste a Spotify playlist URL to start.');
      return;
    }
    setLoading(true);
    setError(null);
    setRevealed({ title: false, artist: false, year: false });
    setHasPlayedOnce(false);

    try {
      const track = await getRandomTrack(
        accessToken,
        selectedPlaylistId || null,
        seenTrackIdsRef.current
      );
      seenTrackIdsRef.current.add(track.id);
      setCurrentSong(track);
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : 'Failed to load song. Make sure you have Spotify tracks in your library.';
      setError(message);
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [accessToken, selectedPlaylistId]);

  useEffect(() => {
    if (selectedPlaylistId) {
      loadNewSong();
    }
  }, [loadNewSong, selectedPlaylistId]);


  const handleRevealTitle = () => {
    setRevealed((prev) => ({ ...prev, title: true }));
  };

  const handleRevealArtist = () => {
    setRevealed((prev) => ({ ...prev, artist: true }));
  };

  const handleRevealYear = () => {
    setRevealed((prev) => ({ ...prev, year: true }));
  };

  const handleRevealAll = () => {
    setRevealed({ title: true, artist: true, year: true });
  };

  const handleNext = () => {
    loadNewSong();
  };

  const applyPlaylistUrl = () => {
    const id = extractPlaylistId(playlistUrl);
    if (!id) {
      setPlaylistUrlError('Enter a valid Spotify playlist URL, URI, or ID.');
      return;
    }
    setPlaylistUrlError('');
    setSelectedPlaylistId(id);
    seenTrackIdsRef.current = new Set();
    setError(null);
    setShowPlaylistSetup(false);
  };

  const handlePlaylistUrlKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      applyPlaylistUrl();
    }
  };

  if (loading) {
    return <div className="game-board loading">Loading song...</div>;
  }

  if (error) {
    return (
      <div className="game-board error">
        <p>{error}</p>
        <button onClick={loadNewSong}>Try Again</button>
      </div>
    );
  }

  return (
    <div className="game-board">
      <h1>Hitster</h1>
      {showPlaylistSetup && (
        <div className="playlist-url">
          <label htmlFor="playlist-url-input">Spotify Playlist URL</label>
          <div className="playlist-url-row">
            <input
              id="playlist-url-input"
              type="text"
              placeholder="https://open.spotify.com/playlist/..."
              value={playlistUrl}
              onChange={(event) => setPlaylistUrl(event.target.value)}
              onKeyDown={handlePlaylistUrlKeyDown}
              disabled={loading}
            />
            <button
              type="button"
              onClick={applyPlaylistUrl}
              disabled={!playlistUrl.trim() || loading}
            >
              Use Playlist
            </button>
          </div>
          {playlistUrlError && <div className="error-message">{playlistUrlError}</div>}
          {!selectedPlaylistId && !loading && !error && (
            <div className="info-text">Paste a Spotify playlist URL to start.</div>
          )}
        </div>
      )}
      {!showPlaylistSetup && (
        <div className="playlist-actions">
          <button
            type="button"
            className="change-playlist-button"
            onClick={() => {
              setShowPlaylistSetup(true);
              setSelectedPlaylistId('');
              setCurrentSong(null);
              setRevealed({ title: false, artist: false, year: false });
              setHasPlayedOnce(false);
              seenTrackIdsRef.current = new Set();
            }}
          >
            Change playlist
          </button>
        </div>
      )}

      {currentSong && (
        <>
          <MusicPlayer
            song={currentSong}
            accessToken={accessToken}
            onPlayStart={() => setHasPlayedOnce(true)}
          />
          <SongInfo
            song={currentSong}
            revealed={revealed}
          />
          <Controls
            onRevealTitle={handleRevealTitle}
            onRevealArtist={handleRevealArtist}
            onRevealYear={handleRevealYear}
            onRevealAll={handleRevealAll}
            onNext={handleNext}
            revealed={revealed}
            canReveal={hasPlayedOnce}
            hasNextSong={true}
            canNext={revealed.title || revealed.artist || revealed.year}
          />
        </>
      )}
    </div>
  );
}
